import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Clock, CheckCircle, Calendar, Star, X } from 'lucide-react';
import mentorService from '@services/mentor.service';
import { toast } from 'react-hot-toast';

const Earnings = () => {
  const [loading, setLoading] = useState(true);
  const [earningsData, setEarningsData] = useState(null);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [processingWithdrawal, setProcessingWithdrawal] = useState(false);

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      const response = await mentorService.getMentorEarnings();
      if (response.success) {
        setEarningsData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
      toast.error('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  const paymentInfo = earningsData?.paymentInfo || {};
  const transactions = earningsData?.recentTransactions || [];
  const hasEarnings = paymentInfo.totalEarned > 0;

  const handleWithdrawalClick = () => {
    const totalEarned = parseFloat(paymentInfo.totalEarned) || 0;
    
    if (totalEarned < 200) {
      toast.error('Minimum withdrawal amount is ₹200');
      return;
    }
    
    setWithdrawalAmount(totalEarned.toString());
    setShowWithdrawalModal(true);
  };

  const handleWithdrawalSubmit = async () => {
    const amount = parseFloat(withdrawalAmount);
    const totalEarned = parseFloat(paymentInfo.totalEarned) || 0;

    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (amount > totalEarned) {
      toast.error('Withdrawal amount cannot exceed total earned');
      return;
    }

    if (amount < 200) {
      toast.error('Minimum withdrawal amount is ₹200');
      return;
    }

    setProcessingWithdrawal(true);
    
    // Simulate withdrawal processing
    setTimeout(() => {
      toast.success(`Withdrawal request of ${formatAmount(amount)} submitted successfully!`);
      setShowWithdrawalModal(false);
      setWithdrawalAmount('');
      setProcessingWithdrawal(false);
      
      // Refresh earnings data
      fetchEarnings();
    }, 1000);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Earnings Overview</h2>
            <p className="text-sm text-gray-500 mt-1">Track your payments and transaction history</p>
          </div>
          <button
            onClick={handleWithdrawalClick}
            disabled={loading || (parseFloat(paymentInfo.totalEarned) || 0) < 200}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              (parseFloat(paymentInfo.totalEarned) || 0) >= 200
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <DollarSign className="w-4 h-4 inline mr-1" />
            Withdraw Funds
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Earnings Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <div className="text-xs text-gray-600 font-medium">Total Earned</div>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatAmount(paymentInfo.totalEarned)}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div className="text-xs text-gray-600 font-medium">Total Paid</div>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatAmount(paymentInfo.totalPaid)}
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div className="text-xs text-gray-600 font-medium">Pending Amount</div>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatAmount(paymentInfo.pendingAmount)}
            </div>
          </div>
        </div>

        {/* Payment Stats */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary-600" />
            Payment Statistics
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {paymentInfo.totalQuestionsPaid || 0}
              </div>
              <div className="text-xs text-gray-600 mt-1">Questions Paid</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {earningsData?.totalQuestions || 0}
              </div>
              <div className="text-xs text-gray-600 mt-1">Total Answered</div>
            </div>
            <div className="text-center col-span-2 md:col-span-1">
              <div className="text-xs text-gray-600 mb-1 flex items-center justify-center gap-1">
                <Calendar className="w-3 h-3" />
                Last Payment
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {paymentInfo.lastPaymentDate ? formatDate(paymentInfo.lastPaymentDate) : 'No payments yet'}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Recent Transactions</h3>
          
          {!hasEarnings ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <DollarSign className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                No earnings yet
              </h3>
              <p className="text-sm text-gray-500">
                Your earnings will be displayed here once you start answering questions
              </p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-500">No recent transactions</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Question
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Student
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Rating
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.map((transaction) => (
                      <tr key={transaction.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {formatDate(transaction.paymentDate)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-xs">
                          <div className="line-clamp-2">
                            {transaction.question?.question || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-semibold text-white">
                                {transaction.question?.student?.firstName?.charAt(0) || 'S'}
                              </span>
                            </div>
                            <span className="text-gray-900">
                              {transaction.question?.student 
                                ? `${transaction.question.student.firstName} ${transaction.question.student.lastName}`
                                : 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-1 bg-green-100 text-green-800 rounded-md text-sm font-semibold">
                            {formatAmount(transaction.paymentAmount)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {transaction.question?.rating ? (
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                              <span className="font-medium text-gray-900">{transaction.question.rating}/5</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">Not rated</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Withdrawal Modal */}
      {showWithdrawalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Withdraw Funds</h3>
              <button
                onClick={() => setShowWithdrawalModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Available Balance
                </label>
                <div className="text-2xl font-bold text-green-600">
                  {formatAmount(paymentInfo.totalEarned)}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Withdrawal Amount
                </label>
                <input
                  type="number"
                  value={withdrawalAmount}
                  onChange={(e) => setWithdrawalAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="200"
                  max={paymentInfo.totalEarned}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum withdrawal amount: ₹200
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Your withdrawal request will be processed within 3-5 business days. 
                  The amount will be transferred to your registered bank account.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowWithdrawalModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleWithdrawalSubmit}
                  disabled={processingWithdrawal}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processingWithdrawal ? 'Processing...' : 'Confirm Withdrawal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Earnings;
